import { useShowInfo } from 'app/shared/context-hooks/use-show-info.hook';
import { LSKey } from 'app/tools/hooks/local-storage/local-storage.types';
import _ from 'lodash';
import { useEffect } from 'react';
import { noop } from 'rxjs';
import { logSentry } from '../errors/error-utils';

type Props = {
  waitingWorker: ServiceWorker | null;
};
export default function UpdateHandler({
  waitingWorker,
}: Props): null {
  const { showSnack } = useShowInfo();

  // This effect handles the confirmation snackbar for when the
  // app update is completed
  useEffect(() => {
    const appUpdated = window.localStorage.getItem(LSKey.APP_UPDATED);
    if (appUpdated) {
      showSnack({
        message: 'App updated successfully',
        alertType: 'success',
      });
      window.localStorage.removeItem(LSKey.APP_UPDATED);
    }
  }, [showSnack]);

  // Handles the queuing of the waiting worker
  useEffect(() => {
    const { serviceWorker } = window.navigator;
    if (waitingWorker && serviceWorker) {
      let onUpdateReady: (event: Event) => void = noop;

      const readyUpdate = () =>
        new Promise<void>((resolve, reject) => {
          onUpdateReady = (event: Event) => {
            const msgType: unknown = _.get(event, 'data.type');
            if (msgType === 'UPDATE_READY') resolve();
          };
          // start listening for confirmation from service worker that
          // update has been made ready
          serviceWorker.addEventListener('message', onUpdateReady);
          // signal service worker to make update ready
          waitingWorker.postMessage({ type: 'SKIP_WAITING' });
          const timeoutErr = new Error(
            'response from service worker timed out (> 5 seconds)'
          );
          // throw error if service worker takes more than 5 secs to
          // reply with confirmation
          setTimeout(() => reject(timeoutErr), 5000);
        }).finally(() => {
          // remove event listener regardless of previous outcome
          serviceWorker.removeEventListener('message', onUpdateReady);
        });

      const prepareAndReload = () => {
        // set app-updated state to show confirmation snackbar on reload
        window.localStorage.setItem(LSKey.APP_UPDATED, 'true');
        // clear redux cache to avoid any issues with updates changing redux
        // welcomed state is now stored in localStorage not redux
        window.localStorage.removeItem(LSKey.REDUX_PERSIST_STORE);
        window.location.reload();
      };

      showSnack({
        autoHide: false,
        message: 'A new version is available!',
        action: {
          text: 'Update',
          callback: () =>
            readyUpdate().then(prepareAndReload).catch(logSentry),
        },
      });
    }
  }, [showSnack, waitingWorker]);
  return null;
}
